/**
 * canSeeApp — single source of truth for which apps a user is allowed to
 * see in any launcher UI (left rail panel, PSI top-right launcher, etc.).
 *
 * Why this file exists: prior to 2026-05-18 there were two independent
 * copies of this logic — one in src/components/AppSidebar.jsx and one in
 * src/pages/Oracle/OracleHome.jsx. They drifted, with the result that the
 * same user saw different apps in the left rail vs the PSI launcher. The
 * function below is now imported by both, so they cannot disagree.
 *
 * Policy by app:
 *   - Universally visible (auth required only): calendar
 *   - Compliance-only (hidden from dept-bound users): privacy, fdr, ppm,
 *     ratify, network_adequacy, priq, workstream, overmind (PSI itself)
 *   - Product-gated, visible to every authenticated user in the org when
 *     the product is purchased: memo (Mandate). Dept users see the tile
 *     and click into Mandate; Mandate enforces view-only for them
 *     server-side. (Operator requirement 2026-05-18: "memo must always
 *     show when purchased".)
 *   - Dept-aware (compliance + dept-bound both visible): risk, coa, cil,
 *     cap, scrubber. CAP additionally requires the cap product.
 *
 * Server-side authorization is independent and authoritative — the gate
 * below controls launcher visibility, not data access.
 */

const COMPLIANCE_TYPES = new Set([
  'compliance_officer',
  'compliance_analyst',
]);

const DEPT_BOUND_TYPES = new Set([
  'department_lead',
  'department_user',
]);

export function canSeeApp(key, user) {
  if (!user) return false;

  // ─── 2026-08-24 role redesign — decided before every per-app rule ─────────
  // The org super user (superuser / org_superadmin) is the IT ACCESS
  // ADMINISTRATOR: they grant and revoke access on People / Onboarding /
  // Team & Access (gated separately in the sidebar) and see NO app tiles.
  // Platform support (superadmin / platform_superuser) sees tiles only
  // while a superuser-approved support-access window is active — the
  // server enforces the same rule on every request.
  const utAll = user.user_type || user.effective_user_type || null;
  const isAccessAdminIdentity = utAll ? utAll === 'org_superadmin' : user.role === 'superuser';
  if (isAccessAdminIdentity) {
    return false;
  }
  const isPlatformSupport = utAll ? utAll === 'platform_superuser' : user.role === 'superadmin';
  if (isPlatformSupport && user.support_grant_active !== true) return false;

  // Primary source: user_type taxonomy (migration 083). Legacy `role` is
  // ONLY a fallback when user_type and effective_user_type are both null
  // — i.e. an old account that was never backfilled.
  const ut = user.user_type || user.effective_user_type || null;
  const isComplianceLevel = (ut && COMPLIANCE_TYPES.has(ut)) || isPlatformSupport;
  const isDeptBound       = ut && DEPT_BOUND_TYPES.has(ut);
  const isLegacyAdmin     = !ut && ['superadmin', 'admin'].includes(user?.role);
  const isLegacyAssociate = !ut && user?.role === 'associate';

  // ─── Universal ───────────────────────────────────────────────────────────
  if (key === 'calendar') return true;

  // ─── Deck Studio — visible to EVERYONE. The server scopes the data feeds
  //     per department and restricts branding writes to compliance-level
  //     users; visibility here is intentionally universal. ─────────────────
  if (key === 'deck_studio') return true;

  // ─── Sentinel (SIU / FWA) — visible to EVERYONE. Compliance officer + analyst
  //     get the full investigation workbench; department users get only the
  //     narrow "records requested from your team" surface. The split is enforced
  //     server-side (requireComplianceOnly) and in the Sentinel app shell. ────
  if (key === 'sentinel') return true;

  // ─── Precision Responsible AI — compliance officer / org superuser, plus
  //     anyone those users explicitly grant through Team & Access (per-app
  //     access, observers). Granted department users and champions get the
  //     propose-and-track surface; the workbench stays compliance-only,
  //     enforced server-side. ────────────────────────────────────────────
  if (key === 'ai_command') {
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    if (user?.app_roles?.ai_command) return true;
    return false;
  }

  // ─── Materials Review — a hidden product (2026-10-10): the organization
  //     must hold it, then compliance level users and granted users see it.
  if (key === 'materials_review') {
    if (!user?.organization_products?.materials_review) return false;
    if (isComplianceLevel) return true;
    if (user?.app_roles?.materials_review) return true;
    return false;
  }

  // ─── Readiness — a hidden product (2026-10-20): the organization must hold
  //     it. Compliance runs the year; department leads and users see "My items".
  if (key === 'readiness') {
    if (!user?.organization_products?.readiness) return false;
    if (isComplianceLevel) return true;
    if (user?.user_type === 'department_lead' || user?.user_type === 'department_user') return true;
    if (user?.app_roles?.readiness) return true;
    return false;
  }

  // ─── Agent (the Compliance Agent, 2026-10-23) — on only when the server
  //     says the organization's switch and the container flag are both on
  //     (session agent_mode); compliance-level users while the audience is
  //     compliance_only, everyone once the user widens it. Phase A shows the
  //     compliance Setup page only, so the tile is compliance-level here.
  if (key === 'agent') {
    if (user?.agent_mode !== true) return false;
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    return false;
  }

  // ─── Audit — visible to EVERYONE (the Sentinel pattern). Compliance officer +
  //     analyst get the full audit workbench; department users get only the
  //     narrow "My audits" request-and-respond surface for audits of their own
  //     department. The split is enforced server-side (the /my-audits routes sit
  //     above the requireComplianceOnly gate) and in the AuditApp shell. ──────
  if (key === 'audit') return true;

  // ─── PSI (overmind) — compliance-only ────────────────────────────────────
  if (key === 'overmind') {
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    return false;
  }

  // ─── Workstream — compliance-only ────────────────────────────────────────
  if (key === 'workstream') {
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    return false;
  }

  // ─── Memo (standalone Mandate) — RETIRED 2026-08-12. ─────────────────────
  //     The standalone app (pcg-mandate / az-mandate.precisioncompliancegroup
  //     .com) was shut down: every revision is deactivated and the host 404s,
  //     so the tile could only ever lead to a dead page. Precision Mandate
  //     ('mandate_native', below) is the replacement and carries the migrated
  //     history. Hard false — NOT product-gated any more, because the org
  //     product flag `organization_products.memo` is still active and would
  //     otherwise keep showing the dead tile.
  //
  //     Do not restore this without first reactivating the container app
  //     (`az containerapp revision activate --revision pcg-mandate--0000155`).
  //     Note the memo product flag is deliberately left ON: it still gates the
  //     legacy user_app_roles bridge and Settings sections, and flipping it
  //     off is a separate, wider decision.
  if (key === 'memo') return false;

  // ─── Precision Mandate (native rebuild) — visible to every authenticated
  //     user, mirroring the standalone Mandate rule (operator decision
  //     2026-08-10, step 2 of cutover). The server scopes what each user can
  //     do: department users see and work only their own assignments;
  //     upload, review, reassign, and admin stay compliance-gated in
  //     routes/mandateNative.js. ─────────────────────────────────────────
  if (key === 'mandate_native') {
    if (isComplianceLevel) return true;
    if (isDeptBound) return true;
    if (isLegacyAdmin || isLegacyAssociate) return true;
    if (user?.app_roles?.memo || user?.app_roles?.mandate_native) return true;
    return false;
  }

  // ─── Privacy — Compliance Officer and above ONLY (access model decided
  //     2026-07-20): analysts get memo upload, risks, audits, issues, but
  //     NOT Privacy. Server mirrors this in routes/privacy/index.js. ───────
  if (key === 'privacy' || key === 'privacy_program') {
    if (ut === 'compliance_analyst') return false;
    if (!ut && isLegacyAssociate) return false;
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    return false;
  }

  // ─── Policy Management — visible to EVERYONE (2026-07-20): compliance
  //     gets the full workbench, department users get the read-only
  //     published-policy Library. The split is enforced server-side in
  //     routes/ppm.js and in the App.js shell. ─────────────────────────────
  if (key === 'ppm') return true;

  // ─── Reporter — visible to EVERYONE (2026-07-23 operator decision, was
  //     compliance-only): department users upload and validate their own
  //     measure files; the engine retains nothing (validate-and-vanish).
  //     Finalizing a submission for HPMS stays compliance-only server-side
  //     (the maker-checker review gate). ───────────────────────────────────
  if (key === 'reporter') return true;

  // ─── RuleIQ (PRiQ) — visible to EVERYONE (2026-07-23 operator decision):
  //     findings and department briefings are routed to departments, so
  //     department users read them directly. Running analyses, deep-dive
  //     chat, and action-item management stay compliance-level, enforced
  //     server-side (requireModuleAccess('priq','write')). ─────────────────
  if (key === 'priq') return true;

  // ─── FDR Oversight — compliance by default, PLUS explicit Team & Access
  //     grants (2026-07-23): a superuser or compliance officer can grant an
  //     individual user (e.g. a vendor manager) access, recorded in
  //     user_app_access, mirrored to app_roles.fdr at sign-in, and enforced
  //     live server-side (viewer grants are read-only). ────────────────────
  if (key === 'fdr') {
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    if (user?.app_roles?.fdr) return true;
    return false;
  }

  // ─── Compliance-only apps (hidden from dept users) ───────────────────────
  if (['ratify', 'universe_requests'].includes(key)) {
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    return false;
  }

  // ─── Network Adequacy — compliance-only, with explicit app_role fallback
  //     gated on can_see_privacy (matches server-side PERMISSIONS.modules
  //     gating in middleware/auth.js). ────────────────────────────────────
  if (key === 'network_adequacy') {
    if (isComplianceLevel) return true;
    if (isLegacyAdmin && !user?.department_id) return true;
    if (user?.app_roles?.network_adequacy && user?.can_see_privacy === true) return true;
    // Provider roster (2026-09-10): the department that owns provider data
    // sees the app with only the roster surfaces. Server re-checks membership.
    if (user?.roster_department_member === true) return true;
    return false;
  }

  // ─── Dept-aware apps (compliance + dept-bound both visible) ──────────────
  if (['risk', 'coa', 'cil', 'cap', 'scrubber', 'scrubber_2029'].includes(key)) {
    if (key === 'cap' && !user?.organization_products?.cap) return false;
    if (isComplianceLevel) return true;
    if (isDeptBound) return true;
    if (isLegacyAdmin || isLegacyAssociate) return true;
    if (user?.app_roles?.[key]) return true;
    return false;
  }

  return false;
}

export default canSeeApp;
